// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FunnelAnalysisPage
 * @package @geenius/analytics/solidjs-css
 * @description Page-level SolidJS CSS composition for configuring and
 * reviewing analytics funnel analysis.
 */

import {
  type AnalyticsEvent,
  analyzeFunnel,
  buildAnalyticsSeoMeta,
  type FunnelResult,
  type FunnelStep,
} from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from "@geenius/ui/solidjs-css";
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { FunnelChart } from "../components/FunnelChart";
import { StatsCard } from "../components/StatsCard";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { FadeIn } from "../lib/FadeIn";
import type { AnalyticsSeoMeta } from "./EventExplorerPage";

export interface FunnelAnalysisPageProps {
  name?: string;
  steps?: FunnelStep[];
  events?: AnalyticsEvent[];
  result?: FunnelResult | null;
  onAnalyze?: (result: FunnelResult) => void;
  isLoading?: boolean;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => unknown;
  class?: string;
}

const AnalyticsSeoHead: Component<{ meta: unknown }> = (props) => {
  void props.meta;
  return null;
};

function formatPercent(value: number | undefined): string {
  return `${(value ?? 0).toFixed(1)}%`;
}

export const FunnelAnalysisPage: Component<FunnelAnalysisPageProps> = (
  props,
) => {
  const [computedResult, setComputedResult] = createSignal<FunnelResult | null>(
    null,
  );
  const copy = createAnalyticsCopy();
  const name = () => props.name ?? copy.t("analytics.funnel.defaultName");
  const activeResult = () => props.result ?? computedResult();
  const canAnalyze = () =>
    (props.events?.length ?? 0) > 0 && (props.steps?.length ?? 0) > 0;
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.t("analytics.seo.funnelTitle"),
    description: copy.t("analytics.seo.funnelDescription"),
    path: "/analytics/funnels",
    keywords: ["analytics", "funnels", "conversion", "events"],
  });
  const stepRows = createMemo(() =>
    (props.steps ?? []).map((step, index) => ({
      ...step,
      order: index + 1,
      propertyCount: Object.keys(step.properties ?? {}).length,
    })),
  );
  const runAnalysis = (): void => {
    const nextResult = analyzeFunnel(
      props.events ?? [],
      props.steps ?? [],
      name(),
    );
    setComputedResult(nextResult);
    props.onAnalyze?.(nextResult);
  };

  return (
    <>
      {props.renderSeoMeta ? (
        props.renderSeoMeta(seoMeta)
      ) : (
        <AnalyticsSeoHead meta={seoMeta} />
      )}
      <div
        class={cx("gn-analytics-page", "gn-analytics-funnel-page", props.class)}
        data-analytics-page="funnel-analysis"
      >
        <FadeIn>
          <header class="gn-analytics-page__header" data-analytics-page-header>
            <h1 class="gn-analytics-page__title">
              {copy.t("analytics.funnel.pageTitle")}
            </h1>
            <p class="gn-analytics-page__subtitle">
              {copy.t("analytics.funnel.pageSubtitle")}
            </p>
          </header>
        </FadeIn>

        <FadeIn
          delay={60}
          class="gn-analytics-dashboard__kpi-grid"
          data-analytics-funnel-kpis
        >
          <StatsCard
            label={copy.t("analytics.funnel.entries")}
            value={activeResult()?.totalEntries.toLocaleString() ?? "0"}
            icon="IN"
          />
          <StatsCard
            label={copy.t("analytics.dashboard.conversions")}
            value={activeResult()?.totalConversions.toLocaleString() ?? "0"}
            icon="OK"
          />
          <StatsCard
            label={copy.t("analytics.dashboard.conversionRate")}
            value={formatPercent(activeResult()?.overallConversionRate)}
            icon="%"
          />
        </FadeIn>

        <div class="gn-analytics-funnel-page__grid">
          <FadeIn delay={120}>
            <Card data-analytics-funnel-builder>
              <CardHeader>
                <CardTitle data-analytics-section-title>{name()}</CardTitle>
              </CardHeader>
              <CardContent class="gn-analytics-funnel-page__steps">
                <Show
                  when={stepRows().length > 0}
                  fallback={
                    <EmptyState title={copy.t("analytics.funnel.noSteps")} />
                  }
                >
                  <For each={stepRows()}>
                    {(step) => (
                      <article
                        class="gn-analytics-funnel-page__step"
                        data-analytics-funnel-step
                      >
                        <span class="gn-analytics-funnel-page__step-index">
                          {step.order}
                        </span>
                        <div>
                          <strong>{step.name}</strong>
                          <p class="gn-analytics-funnel-page__step-meta">
                            {step.event} -{" "}
                            {copy.plural(
                              "analytics.funnel.propertyFilters",
                              step.propertyCount,
                            )}
                          </p>
                        </div>
                      </article>
                    )}
                  </For>
                </Show>
                <div class="gn-analytics-funnel-page__actions">
                  <Button
                    type="button"
                    variant="primary"
                    disabled={!canAnalyze() || props.isLoading}
                    onClick={runAnalysis}
                  >
                    {copy.t("analytics.funnel.analyze")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </FadeIn>

          <FadeIn delay={180}>
            <Show
              when={!props.isLoading}
              fallback={
                <Card data-analytics-funnel-loading>
                  <CardContent class="gn-analytics-funnel-page__loading">
                    <Skeleton height={24} />
                    <Skeleton height={160} />
                  </CardContent>
                </Card>
              }
            >
              <Show
                when={activeResult()}
                fallback={
                  <Card data-analytics-funnel-empty>
                    <CardContent>
                      <EmptyState
                        title={copy.t("analytics.funnel.noAnalysis")}
                      />
                    </CardContent>
                  </Card>
                }
              >
                {(result) => <FunnelChart result={result()} />}
              </Show>
            </Show>
          </FadeIn>
        </div>
      </div>
    </>
  );
};
